import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DialogData } from '../../../domain/common.model';
import { Schedule, ScheduleFlag } from '../../../domain/schedule.model';
import { ScheduleEditorDialogComponent } from '../components/dialog/schedule-editor-dialog/schedule-editor-dialog.component';
import { GrowlService } from '../service/growl.service';
import { ScheduleService } from '../service/schedule.service';
import { DialogAction } from './dialog.action';
import { ScheduleAction } from './schedule.action';

export class ScheduleStateModel {
    list: Schedule[];
    flag: ScheduleFlag;
}

@State<ScheduleStateModel>({
    name: 'schedule',
    defaults: {
        list: [],
        flag: null
    }
})
@Injectable()
export class ScheduleState {

    constructor(private scheduleService: ScheduleService,
                private dialog: MatDialog,
                private growlService: GrowlService) {
    }

    @Selector()
    static getList(state: ScheduleStateModel) {
        return state.list;
    }

    @Selector()
    static getFlag(state: ScheduleStateModel) {
        return state.flag;
    }

    @Action(ScheduleAction.GetAll)
    getAll(context: StateContext<ScheduleStateModel>) {
        return this.scheduleService.getAll()
            .pipe(map(list => context.patchState({list})));
    }

    @Action(ScheduleAction.GetFlag)
    getFlag(context: StateContext<ScheduleStateModel>) {
        return this.scheduleService.getFlag()
            .pipe(
                map(flag => context.patchState({flag})),
                catchError(() => {
                    context.patchState({flag: new ScheduleFlag()});
                    return of(true);
                })
            );
    }

    @Action(ScheduleAction.OpenEditDialog)
    openEditDialog(context: StateContext<ScheduleStateModel>, action: ScheduleAction.OpenEditDialog) {
        const ref = this.dialog.open(ScheduleEditorDialogComponent, {
            width: '690px',
            autoFocus: true,
            panelClass: 'dialog-location',
            disableClose: true,
            data: action.data
        });
        context.dispatch(new DialogAction.AddDialogRef(ref));
    }

    @Action(ScheduleAction.Delete)
    delete(context: StateContext<ScheduleStateModel>, action: ScheduleAction.Delete) {
        const data = new DialogData();
        data.titleLabel = 'TIMETABLE.REMOVE_DIALOG_TITLE';
        data.descriptionLabel = 'TIMETABLE.REMOVE_DIALOG_DESCRIPTION';
        data.onConfirm = () => {
            return this.scheduleService.delete(action.id)
                .pipe(tap(() => {
                    this.growlService.showInfoGrowl('SCHEDULE.ENTRY_DELETED');
                    context.dispatch(new ScheduleAction.GetAll())
                }));
        };
        context.dispatch(new DialogAction.OpenGeneric(data));
    }

    @Action(ScheduleAction.Save)
    save(context: StateContext<ScheduleStateModel>, action: ScheduleAction.Save) {
        return this.scheduleService.save(action.schedule)
            .pipe(tap(() => {
                this.growlService.showInfoGrowl(action.schedule.id
                    ? 'SCHEDULE.ENTRY_EDITED'
                    : 'SCHEDULE.ENTRY_ADDED');


                context.dispatch([
                    new DialogAction.CloseAll(),
                    new ScheduleAction.GetAll(),
                ]);
            }));

    }
}
